import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import ConfirmDeleteDialog from '../../components/admin/ConfirmDeleteDialog';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type SuperWorkspaceDetail } from '../../lib/api';
import { relativeTime, stripScheme } from '../../lib/dashboardHelpers';
import { C2Button } from '../../components/c2';

export default function AdminWorkspaceDetailPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<SuperWorkspaceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!gate.ready || !id) return;
    api
      .superWorkspace(id)
      .then(setData)
      .catch(err => setError((err as Error).message || 'Failed to load workspace'));
  }, [gate.ready, id]);

  if (!gate.ready) return null;

  const handleDelete = async () => {
    if (!id) return;
    await api.superDeleteWorkspace(id);
    navigate('/admin/workspaces', { replace: true });
  };

  return (
    <AdminShell
      title={data ? data.workspace.domain : 'Workspace'}
      subtitle={
        data
          ? `${data.workspace.is_personal ? 'Personal' : 'Team'} workspace · created ${relativeTime(
              data.workspace.created_at,
            )}`
          : undefined
      }
      rightSlot={
        <Link
          to="/admin/workspaces"
          className="text-[13px] font-semibold no-underline"
          style={{ color: 'var(--color-ink-soft)' }}
        >
          ← All workspaces
        </Link>
      }
    >
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!data ? (
        <div className="text-ink-soft" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* Stats tiles */}
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              marginBottom: 24,
            }}
          >
            <MiniStat label="Total clicks" value={data.stats.totalClicks} />
            <MiniStat label="Clicks 7d" value={data.stats.clicks7d} />
            <MiniStat label="Clicks 30d" value={data.stats.clicks30d} />
            <MiniStat label="Users" value={data.stats.totalUsers} />
            <MiniStat label="Extension users" value={data.stats.extensionUsers} />
          </div>

          {/* Members */}
          <SectionCard title={`Members (${data.users.length})`}>
            <Header cols="2fr 80px 60px 80px 110px 110px">
              <span>Email</span>
              <span>Role</span>
              <span>Links</span>
              <span>Clicks</span>
              <span>Last seen</span>
              <span>Joined</span>
            </Header>
            {data.users.length === 0 ? (
              <Empty>No members.</Empty>
            ) : (
              data.users.map(u => (
                <Row
                  key={u.id}
                  cols="2fr 80px 60px 80px 110px 110px"
                  onClick={() => navigate(`/admin/users/${u.id}`)}
                >
                  <span className="text-sm text-ink font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                    {u.email}
                  </span>
                  <span className="text-xs text-ink-muted">{u.role}</span>
                  <span className="text-sm tabular-nums">{u.link_count}</span>
                  <span className="text-sm tabular-nums">{u.click_count}</span>
                  <span className="text-xs text-ink-muted">
                    {u.extension_last_seen ? relativeTime(u.extension_last_seen) : '—'}
                  </span>
                  <span className="text-xs text-ink-muted">{relativeTime(u.created_at)}</span>
                </Row>
              ))
            )}
          </SectionCard>

          {/* Links */}
          <SectionCard title={`Links (${data.links.length})`}>
            <Header cols="180px 1fr 1fr 80px 110px">
              <span>Slug</span>
              <span>Destination</span>
              <span>Creator</span>
              <span>Clicks</span>
              <span>Created</span>
            </Header>
            {data.links.length === 0 ? (
              <Empty>No links yet.</Empty>
            ) : (
              data.links.map(l => (
                <Row key={l.id} cols="180px 1fr 1fr 80px 110px">
                  <span className="font-mono text-sm font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap">
                    go/{l.slug}
                  </span>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="font-mono text-xs no-underline overflow-hidden text-ellipsis whitespace-nowrap"
                    style={{ color: 'var(--color-ink-soft)' }}
                  >
                    {stripScheme(l.url)}
                  </a>
                  <span className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    {l.creator_email ?? '—'}
                  </span>
                  <span className="text-sm tabular-nums">{l.click_count}</span>
                  <span className="text-xs text-ink-muted">{relativeTime(l.created_at)}</span>
                </Row>
              ))
            )}
          </SectionCard>

          {/* Danger zone */}
          <div
            className="bg-surface"
            style={{
              padding: 20,
              border: '1px solid var(--color-peach)',
              borderRadius: 16,
              marginTop: 8,
            }}
          >
            <h3
              className="font-display font-bold m-0 mb-1"
              style={{ fontSize: 16, color: 'var(--color-peach-deep)' }}
            >
              Danger zone
            </h3>
            <p className="text-sm text-ink-soft m-0 mb-3">
              Deleting this workspace removes all of its users, links, and click history. Not
              reversible.
            </p>
            <C2Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              style={{ color: 'var(--color-peach-deep)' }}
            >
              Delete workspace
            </C2Button>
          </div>

          <ConfirmDeleteDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            title="Delete workspace"
            description={`This will permanently delete ${data.workspace.domain} and all its data.`}
            confirmPhrase={data.workspace.domain}
            consequences={[
              `${data.users.length} user${data.users.length === 1 ? '' : 's'} deleted`,
              `${data.links.length} link${data.links.length === 1 ? '' : 's'} deleted`,
              `${data.stats.totalClicks.toLocaleString()} click record${
                data.stats.totalClicks === 1 ? '' : 's'
              } deleted`,
              'Not reversible',
            ]}
            onConfirm={handleDelete}
          />
        </>
      )}
    </AdminShell>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div
      className="bg-surface"
      style={{
        padding: 14,
        border: '1px solid var(--color-hairline)',
        borderRadius: 12,
      }}
    >
      <div
        className="font-semibold uppercase mb-1"
        style={{ fontSize: 10, color: 'var(--color-ink-muted)', letterSpacing: '0.06em' }}
      >
        {label}
      </div>
      <div className="text-[22px] font-bold text-ink tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="bg-surface overflow-hidden mb-6"
      style={{
        border: '1px solid var(--color-hairline)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--color-hairline)',
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--color-ink)',
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Header({ cols, children }: { cols: string; children: React.ReactNode }) {
  return (
    <div
      className="grid bg-bg-alt"
      style={{
        gridTemplateColumns: cols,
        gap: 12,
        padding: '8px 20px',
        borderBottom: '1px solid var(--color-hairline)',
        fontSize: 10.5,
        fontWeight: 700,
        color: 'var(--color-ink-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {children}
    </div>
  );
}

function Row({
  cols,
  onClick,
  children,
}: {
  cols: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      className={`grid items-center ${onClick ? 'cursor-pointer' : ''} transition-colors duration-150`}
      style={{
        gridTemplateColumns: cols,
        gap: 12,
        padding: '10px 20px',
        borderBottom: '1px solid var(--color-hairline)',
        background: 'var(--color-surface)',
      }}
      onMouseEnter={e => {
        if (onClick) e.currentTarget.style.background = 'var(--color-bg)';
      }}
      onMouseLeave={e => {
        if (onClick) e.currentTarget.style.background = 'var(--color-surface)';
      }}
    >
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-ink-muted text-sm" style={{ padding: 32, textAlign: 'center' }}>
      {children}
    </div>
  );
}
